import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { observer, inject } from 'mobx-react';
import { action, observable, reaction } from 'mobx';
import { getIndex } from 'utils/helpers';
import { authorize } from 'utils/auth';
import { getYesNoValue } from 'api/helpers';
import {
	PRODUCT_GROUPS_CODES,
	INTERNAL_ERROR,
	BAD_REQUEST,
} from 'config/constants';
import PaginationOptions from 'utils/domain/PaginationOptions';
import { Redirect } from 'react-router-dom';
import {
	InfoBox,
	DataTable,
	Loader,
	PageHeader,
	ItemContentHeader,
	ChooseColor,
	ErrorLoadingContent,
} from 'ui/components';
import {
	render_amount,
	render_date,
	render_solo_item,
	render_yes_no,
	render_editable_number,
	render_quantity,
} from 'utils';
import SortingOptions from 'utils/domain/SortingOptions';
import { FormattedMessage } from 'ui/i18n';

import './ProductGroupPage.scss';

function getColumns(toggleModal, onChangeQty, onBlurHandler) {
	return [
		{
			prop: 'number',
			title: <FormattedMessage id="itemsTable.soloItemColumn" />,
			sortable: true,
			render(row) {
				return render_solo_item(row[this.prop], row, toggleModal);
			},
		},
		{
			prop: 'promotion_name',
			title: <FormattedMessage id="itemsTable.promoNameColumn" />,
			align: 'left',
			sortable: true,
		},
		{
			prop: 'end_date',
			title: <FormattedMessage id="itemsTable.endDateColumn" />,
			align: 'center',
			sortable: true,
			render(row) {
				return render_date(row[this.prop]);
			},
			noWrap: true,
		},
		{
			prop: 'quantity',
			title: <FormattedMessage id="itemsTable.qtyColumn" />,
			align: 'center',
			sortable: true,
			render(row) {
				return render_quantity(row[this.prop]);
			},
		},
		{
			prop: 'list_price',
			title: <FormattedMessage id="itemsTable.listColumn" />,
			align: 'right',
			sortable: true,
			render(row) {
				return render_amount(row[this.prop], row);
			},
			noWrap: true,
		},
		{
			prop: 'promo',
			title: <FormattedMessage id="itemsTable.promoColumn" />,
			sortable: true,
			align: 'right',
			render(row) {
				return render_amount(row[this.prop], row);
			},
			noWrap: true,
		},
		{
			prop: 'ta',
			title: <FormattedMessage id="itemsTable.TAColumn" />,
			sortable: true,
			render(row) {
				return `${row[this.prop]}%`;
			},
			align: 'right',
		},
		{
			prop: 'net_price',
			title: <FormattedMessage id="itemsTable.netColumn" />,
			align: 'right',
			sortable: true,
			render(row) {
				return render_amount(row[this.prop], row);
			},
			noWrap: true,
		},
		{
			prop: 'est_ship_date',
			title: <FormattedMessage id="itemsTable.estShipColumn" />,
			align: 'center',
			sortable: true,
			render(row) {
				return render_date(row[this.prop]);
			},
		},
		{
			prop: 'invoice_hold_weeks',
			title: <FormattedMessage id="itemsTable.numberHoldInvWksColumn" />,
			align: 'center',
			sortable: true,
		},
		{
			prop: 'is_drop_ship',
			title: <FormattedMessage id="itemsTable.dropShipColumn" />,
			align: 'center',
			sortable: true,
			render(row) {
				return render_yes_no(getYesNoValue(row[this.prop]));
			},
		},
		{
			prop: 'id',
			title: <FormattedMessage id="itemsTable.addToCartQtyColumn" />,
			align: 'center',
			render(row) {
				return render_editable_number(
					row,
					null,
					false,
					onChangeQty,
					onBlurHandler
				);
			},
		},
	];
}

@authorize({ rights: ['GetByProductGroupCode'] })
@inject('ProductsState', 'AppState', 'CartState', 'CartItemsState')
@observer
export default class ProductGroupPage extends Component {
	static propTypes = {
		ProductsState: PropTypes.object.isRequired,
		AppState: PropTypes.object.isRequired,
		CartState: PropTypes.object.isRequired,
		CartItemsState: PropTypes.object.isRequired,
		match: PropTypes.object.isRequired,
	};

	constructor(props) {
		super(props);

		reaction(
			() => [this.GroupCode, this.pagination, this.order],
			() =>
				this.ProductsState.fetchByProductGroup({
					group_code: this.GroupCode,
					sorting: this.order,
					pagination: this.pagination,
				})
		);
	}

	componentWillMount() {
		this.setProductCode();
	}

	componentWillUnmount() {
		this.CartItemsState.emptyCurrentCart();
	}

	@observable order = new SortingOptions('number', 'asc');
	@observable pagination = new PaginationOptions();
	@observable GroupCode = null;
	@observable showModal = false;

	ProductsState = this.props.ProductsState;
	AppState = this.props.AppState;
	CartState = this.props.CartState;
	CartItemsState = this.props.CartItemsState;

	onChangeQty(value, item) {
		if (value) {
			item.quantity_ordered = value;
			item.errorMsgId = this.CartItemsState.getErrorMessageId(
				false,
				this.CartState.getAvailableQuantity(item, false),
				this.CartState.getTotalQuantityFromCart(item),
				item
			);
			this.CartItemsState.addToCurrentCart(item);
		} else {
			this.CartItemsState.deleteFromCart(item);
		}
	}

	onBlurHandler(item) {
		const index = getIndex(
			this.CartItemsState.cartUpdates,
			'number',
			item.number
		);

		if (
			index >= 0 &&
			item.is_configurable &&
			item.quantity_ordered &&
			!item.errorMsgId
		) {
			this.toggleModal(item);
		}
	}

	@action
	setProductCode() {
		this.GroupCode =
			PRODUCT_GROUPS_CODES[this.props.match.params.GroupCodeDescription];
	}

	@action
	onOrderChange(order) {
		this.order = order;
	}

	@action
	handlePaginationChange(pagination) {
		this.pagination = pagination;
	}

	@action
	toggleModal(itemDetails) {
		this.itemDetails = itemDetails;
		this.ProductsState.fetchByProductGroup({
			group_code: this.GroupCode,
			sorting: this.order,
			pagination: this.pagination,
		});
		this.showModal = !this.showModal;
	}

	@action
	handleAddToCart() {
		this.showModal = false;

		if (this.CartItemsState.isCurrentCartEmpty) {
			this.CartItemsState.toggleWarningMessage();
		} else {
			this.CartState.addToCart(this.CartItemsState.cartUpdates).then(() => {
				this.CartItemsState.emptyCurrentCart();

				this.ProductsState.fetchByProductGroup({
					group_code: this.GroupCode,
					sorting: this.order,
					pagination: this.pagination,
					clearCache: true,
				});
			});
		}
	}

	render() {
		const productGroupCode = this.props.match.params.GroupCodeDescription;

		return (
			<div className="ProductGroupPage">
				{!this.GroupCode && <Redirect to="/" />}

				<PageHeader
					headerText={
						<span>
							<FormattedMessage id={productGroupCode} />
						</span>
					}
				/>

				{this.ProductsState.fetchByProductGroup.handle({
					loading: () => <Loader />,
					rejected: error =>
						<div>
							{error.status === INTERNAL_ERROR || error.status === BAD_REQUEST
								? <ErrorLoadingContent />
								: <InfoBox type="danger">
										<FormattedMessage id={error.message} />
									</InfoBox>}
						</div>,
					resolved: products =>
						<div className="ProductGroupPage__content">
							{products.data.length
								? <div>
										<ItemContentHeader
											handleAddToCart={this.handleAddToCart.bind(this)}
											disableAddToCart={
												this.CartItemsState.hasError ||
												this.CartState.addToCart.state.loading ||
												products.data[0].is_preview
											}
										>
											<div className="col-md-6 ">
												<p id="ProductGroup-count">
													{products.count}{' '}
													<FormattedMessage id="productGroup.itemsIn" />{' '}
													<strong>
														<FormattedMessage id={productGroupCode} />
													</strong>
												</p>
											</div>
										</ItemContentHeader>

										<DataTable
											id="ProductGroup-results-table"
											columns={getColumns(
												this.toggleModal.bind(this),
												this.onChangeQty.bind(this),
												this.onBlurHandler.bind(this)
											)}
											rows={products.data}
											order={this.order}
											onOrderChange={this.onOrderChange.bind(this)}
											count={products.count}
											pagination={this.pagination}
											onPaginationChange={this.handlePaginationChange.bind(
												this
											)}
										/>

										{this.showModal &&
											<ChooseColor
												show={this.showModal}
												onRequestClose={this.toggleModal.bind(this)}
												item={this.itemDetails}
												handleAddToCart={this.handleAddToCart.bind(this)}
											/>}
									</div>
								: <InfoBox type="info">
										<FormattedMessage id="general.noItemsAvailable" />
									</InfoBox>}
						</div>,
				})}
			</div>
		);
	}
}



// WEBPACK FOOTER //
// ./src/ui/pages/ProductGroup/index.js